import { useState, useEffect, MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { styled, useTheme, Theme } from '@mui/material/styles';
import { Box, IconButton, List, ListItem, ListItemText, Menu, MenuItem } from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';

// import Avatar from 'components/@extended/Avatar';
import useAuth from 'hooks/useAuth';
import { useGetMenuMaster } from 'api/menu';
import { DASHBOARD_MODE_EVENT, DashboardModes, getDashboardMode } from 'utils/dashboardMode';

import { RightOutlined } from '@ant-design/icons';
import { useConsultantProfile } from '../../../../api/consultant';

interface ExpandMoreProps {
  expand: boolean;
  drawerOpen: boolean;
}

const ExpandMore = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'expand' && prop !== 'drawerOpen'
})<ExpandMoreProps>(({ theme, expand, drawerOpen }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(-90deg)',
  marginLeft: 'auto',
  color: theme.palette.secondary.dark,
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  }),
  ...(!drawerOpen && {
    opacity: 0,
    width: 50,
    height: 50
  })
}));

interface UserInformation {
  preferedName?: string;
  preferredName?: string;
  name?: string;
  firstName?: string;
  username?: string;
  lastName?: string;
  cityName?: string;
  Consultant?: {
    name?: string;
    isConsultant?: boolean;
    logo?: string;
    logoUrl?: string;
  };
  isConsultant?: boolean;
  consultant?: {
    name?: string;
    logo?: string;
  };
  consultantName?: string;
  companyName?: string;
}


const NavUser: React.FC = () => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened ?? true;

  const { logout, user } = useAuth();
  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, { state: { from: '' } });
    } catch (err) {
      console.error(err);
    }
  };
type DashboardModesType = typeof DashboardModes[keyof typeof DashboardModes];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const getStoredUser = () => {
    if (typeof window === 'undefined') return null;
    try {
      const rawInfo = localStorage.getItem('userInformation');
      const rawAuth = localStorage.getItem('authUser');
      const parsedInfo = rawInfo ? (JSON.parse(rawInfo) as UserInformation) : null;
      const parsedAuth = rawAuth ? (JSON.parse(rawAuth) as UserInformation) : null;
      return parsedInfo || parsedAuth;
    } catch (e) {
      console.error('Error parsing userInformation from localStorage:', e);
      return null;
    }
  };

  const [userInformation, setUserInformation] = useState<UserInformation | null>(() => {
    const stored = getStoredUser();
    if ((stored as any)?.city) return stored;
    if (user) return user as UserInformation;
    return stored;
  });
  const [dashboardMode, setDashboardMode] = useState<DashboardModesType | null>(() =>
    typeof window === 'undefined' ? null : getDashboardMode()
  );

  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleStorageChange = () => {
      try {
        setUserInformation(getStoredUser());
      } catch (e) {
        console.error('Error parsing userInformation from localStorage:', e);
        setUserInformation(null);
      }
    };
    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (user) {
      setUserInformation((prev) => {
        if ((prev as any)?.city) return prev;
        return user as UserInformation;
      });
    }
  }, [user]);

  useEffect(() => {
    const handleModeChange = (event: CustomEvent) => {
      const next: DashboardModesType = event?.detail?.mode ?? getDashboardMode();
      setDashboardMode(next);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(DASHBOARD_MODE_EVENT, handleModeChange as EventListener);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(DASHBOARD_MODE_EVENT, handleModeChange as EventListener);
      }
    };
  }, []);

  const currentUser = (user as UserInformation | undefined) || userInformation;

  const displayName =
    currentUser?.preferedName ||
    currentUser?.preferredName ||
    currentUser?.name ||
    currentUser?.firstName ||
    currentUser?.username ||
    currentUser?.lastName ||
    '';

  const consultantLabel =
    userInformation?.Consultant?.name ||
    userInformation?.consultant?.name ||
    userInformation?.consultantName ||
    userInformation?.companyName ||
    '';
  const secondaryLabel = dashboardMode === DashboardModes.CONSULTANT ? consultantLabel : userInformation?.cityName || '';
  const isConsultantUser = Boolean(userInformation?.Consultant?.isConsultant || userInformation?.isConsultant);

  const { consultant } = useConsultantProfile({ enabled: isConsultantUser });
  const consultantLogo = consultant?.logo || userInformation?.Consultant?.logo || userInformation?.Consultant?.logoUrl || '';

  return (
    <Box sx={{ p: 1.25, px: !drawerOpen ? 1.25 : 3, borderTop: `2px solid ${theme.palette.divider}` }}>
      <List disablePadding>
        <ListItem
          disablePadding
          secondaryAction={
            drawerOpen ? (
              <ExpandMore
                size="small"
                expand={open}
                drawerOpen={drawerOpen}
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                aria-label="show more"
              >
                <RightOutlined style={{ fontSize: '0.625rem' }} />
              </ExpandMore>
            ) : null
          }
          sx={{
            '& .MuiListItemSecondaryAction-root': { right: !drawerOpen ? -20 : -16 },
            justifyContent: drawerOpen ? 'flex-start' : 'center'
          }}
        >
          {drawerOpen ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isConsultantUser && consultantLogo && (
                <img src={consultantLogo} alt="Consultant logo" style={{ height: 22, width: 'auto', objectFit: 'contain' }} />
              )}
              <ListItemText primary={displayName} secondary={secondaryLabel} />
            </Box>
          ) : (
            <IconButton
              size="large"
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              sx={{
                color: theme.palette.text.primary,
                borderRadius: '50%',
                width: 44,
                height: 44
              }}
            >
              <AccountCircleOutlinedIcon fontSize="inherit" />
            </IconButton>
          )}
        </ListItem>
      </List>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
        <MenuItem component={Link} to="/profiles/account/basic" onClick={handleClose}>
          My account
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NavUser;
